import { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import InputError from "components/InputError";

const TOOLBAR = [
	[{ size: ["small", false, "large"] }],
	[{ header: [1, 2, 3, 4, 5, false] }],
	["bold", "italic", "underline"],
	["blockquote"],
	[{ list: "ordered" }, { list: "bullet" }],
];

const normalizeHtml = (html) => {
	if (!html || html === "<p><br></p>") {
		return "";
	}
	return html;
};

const RichTextEditor = ({ value, onTextChange, error, height, resetKey, label = "Rich text editor" }) => {
	const wrapperRef = useRef(null);
	const quillRef = useRef(null);
	const onTextChangeRef = useRef(onTextChange);

	onTextChangeRef.current = onTextChange;

	useEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) {
			return undefined;
		}

		wrapper.innerHTML = "";

		const editorElement = document.createElement("div");
		editorElement.style.height = height ?? "150px";
		wrapper.appendChild(editorElement);

		const quill = new Quill(editorElement, {
			theme: "snow",
			modules: { toolbar: TOOLBAR },
		});

		quillRef.current = quill;
		quill.root.setAttribute("role", "textbox");
		quill.root.setAttribute("aria-multiline", "true");
		quill.root.setAttribute("aria-label", label);
		const toolLabels = { bold: "Bold", italic: "Italic", underline: "Underline", blockquote: "Block quote" };
		wrapper.querySelectorAll(".ql-toolbar button").forEach((button) => {
			const tool = Object.keys(toolLabels).find((key) => button.classList.contains(`ql-${key}`));
			const title = toolLabels[tool] || (button.value === "ordered" ? "Numbered list" : "Bulleted list");
			button.setAttribute("aria-label", title);
			button.setAttribute("title", title);
		});
		wrapper.querySelector(".ql-size .ql-picker-label")?.setAttribute("aria-label", "Text size");
		wrapper.querySelector(".ql-header .ql-picker-label")?.setAttribute("aria-label", "Heading level");

		const initialHtml = value ?? "";
		if (initialHtml) {
			quill.clipboard.dangerouslyPasteHTML(initialHtml);
		}

		const handleChange = () => {
			onTextChangeRef.current?.({
				htmlValue: normalizeHtml(quill.root.innerHTML),
				textValue: quill.getText(),
			});
		};

		quill.on("text-change", handleChange);

		return () => {
			quill.off("text-change", handleChange);
			quillRef.current = null;
			wrapper.innerHTML = "";
		};
		// Re-create the editor only when resetKey changes (e.g. a different book is opened).
		// Parent rerenders must not reinitialize Quill or the caret will disappear.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetKey]);

	useEffect(() => {
		quillRef.current?.root.setAttribute("aria-invalid", String(!!error));
		quillRef.current?.root.setAttribute("aria-label", label);
	}, [error, label, resetKey]);

	return (
		<div className="relative rich-text-editor">
			<div ref={wrapperRef} className="bg-white rounded border border-gray-200" />
			{error && <InputError error={error} />}
		</div>
	);
};

export default RichTextEditor;
